import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { fetchInvitationQuery, recordOpenedDashboard } from "../../behavior/api/invitation";
import { Heading } from "../../components/elements";
import type { LargeAlertProps } from "../../components/elements/LargeAlert";
import LargeAlert from "../../components/elements/LargeAlert";
import { PageRoute } from "../../constants";
import type { Invitation } from "../../types/invitation";
import "./ProcessInvitation.css";

const ProcessInvitation = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();
    const [alertData, setAlertData] = useState<LargeAlertProps>({
        title: t("ProcessInvitation.loading-title"),
        subtitle: t("ProcessInvitation.loading-subtitle"),
        type: "info",
    });
    const {
        data: invitation,
        isLoading: loading,
        error,
    } = useQuery<Invitation>({
        queryKey: ["invitation", token],
        queryFn: fetchInvitationQuery(token),
        enabled: !!token,
    });

    const [interviewExpired, setInterviewExpired] = useState(false);

    useEffect(() => {
        function isValid(invitationData: Invitation) {
            const type = invitationData.__typename ?? invitationData.type;
            if (type === "Invitation") {
                if (invitationData.interviewCompleted) {
                    setAlertData({
                        title: t("ProcessInvitation.InterviewCompleted"),
                        subtitle: "",
                        type: "info",
                    });
                    return false;
                } else {
                    return true;
                }
            } else {
                if (type === "InvitationExpired") {
                    setInterviewExpired(true);
                }
                setAlertData({
                    title: t(`ProcessInvitation.${type}`),
                    subtitle: "",
                    type: "info",
                });
                return false;
            }
        }

        const navigateToPreInterview = async (invitationData: Invitation) => {
            if (invitationData.invitee.providerData && invitationData.invitee.providerData.length > 0) {
                navigate(`${PageRoute.PRE_INTERVIEW_LOGIN}?token=${token}`, {
                    replace: true,
                });
            } else {
                const urlSearchParams = new URLSearchParams({
                    interviewId: invitationData.publicInterviewId ?? invitationData.interviewId,
                    redirectUrl: `${PageRoute.POST_INTERVIEW_REGISTRATION}?token=${token}`,
                    displayName: invitationData.invitee.displayName,
                });
                navigate(`${PageRoute.PRE_INTERVIEW}?${urlSearchParams}`, {
                    replace: true,
                });
            }
        };

        if (!loading) {
            if (error) {
                setAlertData({
                    title: t("AppMessages.server-error"),
                    subtitle: "",
                    type: "info",
                });
            } else if (invitation && isValid(invitation) && token) {
                recordOpenedDashboard({ token });
                navigateToPreInterview(invitation);
            }
        }
    }, [invitation, error, loading, token, navigate, t]);

    if (!token) {
        navigate(PageRoute.ROOT, { replace: true });
    }

    return (
        <div className="process-invitation-wrapper">
            {interviewExpired ? (
                <LargeAlert title={alertData.title} subtitle={alertData.subtitle} type={alertData.type}>
                    <Heading level="h4" light>
                        {t(`ProcessInvitation.InvitationExpired_Description1`)}{" "}
                        <Link to={PageRoute.INTERVIEWS} className="subtitle-link">
                            {t("General.Title_MyHubert")}
                        </Link>{" "}
                        {t(`ProcessInvitation.InvitationExpired_Description2`)}
                    </Heading>
                </LargeAlert>
            ) : (
                <LargeAlert
                    title={alertData.title}
                    subtitle={alertData.subtitle}
                    type={alertData.type}
                    button={{
                        label: t("General.Button_GoTo_MyHubert"),
                        to: PageRoute.ROOT,
                    }}
                />
            )}
        </div>
    );
};

export default ProcessInvitation;
